import { useI18n } from '../lib/use-i18n.js';

/**
 * Crafted product mockups — pure HTML/CSS/SVG device frames showing realistic,
 * live-looking Kilat product UI. No screenshots, no images: everything is
 * themeable DOM so it stays crisp at any zoom and reads correctly in every
 * locale (all chrome routes through `useI18n`).
 *
 *   - `QueueBoardMock`   — the operator dashboard's live queue board (tablet).
 *   - `CustomerPhoneMock`— a white-label customer PWA (phone) rendered in a
 *     DELIBERATELY non-Kilat accent to make the "your brand, not ours" point.
 *   - `AgentPhoneMock`   — the Kilat sales-agent app (phone).
 *
 * Sample ticket/plate/amount values are illustrative fixtures (a mock screen),
 * kept as module constants and rendered via expressions — the meaningful chrome
 * (column titles, actions, labels) is translated.
 */

// ── sample fixtures (illustrative product data, not translatable copy) ──────
const QUEUE_WAITING = [
  { plate: 'WXY 8821', svc: 'svcPremium', eta: 12 },
  { plate: 'VBA 4409', svc: 'svcExpress', eta: 20 },
] as const;
const QUEUE_WASHING = [
  { plate: 'JKL 1207', svc: 'svcInterior', bay: 2, progress: 0.72 },
  { plate: 'PQR 3345', svc: 'svcWaxPolish', bay: 1, progress: 0.38 },
] as const;
const QUEUE_READY = [{ plate: 'BKV 5590', svc: 'svcPremium' }] as const;

const CUSTOMER_ACCENT = '#6d5efc'; // a demo operator's OWN brand colour (white-label proof)
const CUSTOMER_BRAND = 'Auto Shine';

const metaChip: React.CSSProperties = {
  fontSize: 9.5,
  fontWeight: 700,
  padding: '2px 7px',
  borderRadius: 999,
  background: 'var(--color-surface-2)',
  color: 'var(--color-on-surface-muted)',
  whiteSpace: 'nowrap',
};

// ────────────────────────────────────────────────────────────────────────────
// Queue board (tablet)
// ────────────────────────────────────────────────────────────────────────────

export function QueueBoardMock({ className }: { readonly className?: string }) {
  const { t } = useI18n();
  return (
    <div
      className={className}
      style={{
        width: '100%',
        maxWidth: 560,
        aspectRatio: '4 / 3',
        borderRadius: 24,
        padding: 12,
        background: 'linear-gradient(160deg, #1a1d24, #0c0e12)',
        boxShadow: '0 40px 90px -40px rgba(0,0,0,0.7), inset 0 0 0 1px rgba(255,255,255,0.06)',
      }}
      role="img"
      aria-label={t('marketing.home.mock.boardTitle')}
    >
      <div
        style={{
          height: '100%',
          borderRadius: 16,
          background: 'var(--color-bg)',
          border: '1px solid var(--hairline)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* board header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '10px 14px',
            borderBottom: '1px solid var(--hairline)',
            background: 'var(--color-surface)',
          }}
        >
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <BoltGlyph />
            <span style={{ fontWeight: 700, fontSize: 13, color: 'var(--color-on-surface)' }}>
              {t('marketing.home.mock.boardTitle')}
            </span>
          </span>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 11,
              fontWeight: 600,
              color: 'var(--kilat-lume)',
            }}
          >
            <span
              aria-hidden
              style={{
                width: 7,
                height: 7,
                borderRadius: 999,
                background: 'var(--kilat-lume)',
                boxShadow: '0 0 0 3px var(--kilat-lume-soft)',
              }}
            />
            {t('marketing.home.mock.live')}
          </span>
        </div>

        {/* columns */}
        <div
          style={{
            flex: 1,
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 8,
            padding: 10,
            minHeight: 0,
          }}
        >
          <QueueColumn title={t('marketing.home.mock.colWaiting')} count={QUEUE_WAITING.length}>
            {QUEUE_WAITING.map((tk) => (
              <TicketCard key={tk.plate} plate={tk.plate} svc={t(`marketing.home.mock.${tk.svc}`)}>
                <span style={metaChip}>{t('marketing.home.mock.etaMin', { n: tk.eta })}</span>
              </TicketCard>
            ))}
          </QueueColumn>

          <QueueColumn
            title={t('marketing.home.mock.colWashing')}
            count={QUEUE_WASHING.length}
            accent
          >
            {QUEUE_WASHING.map((tk) => (
              <TicketCard key={tk.plate} plate={tk.plate} svc={t(`marketing.home.mock.${tk.svc}`)}>
                <span style={metaChip}>{t('marketing.home.mock.bayN', { n: tk.bay })}</span>
                <ProgressBar value={tk.progress} />
              </TicketCard>
            ))}
          </QueueColumn>

          <QueueColumn title={t('marketing.home.mock.colReady')} count={QUEUE_READY.length}>
            {QUEUE_READY.map((tk) => (
              <TicketCard
                key={tk.plate}
                plate={tk.plate}
                svc={t(`marketing.home.mock.${tk.svc}`)}
                ready
              >
                <span
                  style={{
                    ...metaChip,
                    color: '#04140d',
                    background: 'var(--kilat-lume)',
                    fontWeight: 700,
                  }}
                >
                  {t('marketing.home.mock.notify')}
                </span>
              </TicketCard>
            ))}
          </QueueColumn>
        </div>
      </div>
    </div>
  );
}

function QueueColumn({
  title,
  count,
  accent = false,
  children,
}: {
  readonly title: string;
  readonly count: number;
  readonly accent?: boolean;
  readonly children: React.ReactNode;
}) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        borderRadius: 12,
        padding: 8,
        background: accent ? 'var(--kilat-lume-soft)' : 'var(--color-surface-2)',
        minHeight: 0,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span
          style={{
            fontSize: 10.5,
            fontWeight: 700,
            letterSpacing: 0.6,
            textTransform: 'uppercase',
            color: 'var(--color-on-surface-muted)',
          }}
        >
          {title}
        </span>
        <span
          aria-hidden
          style={{
            fontSize: 10,
            fontWeight: 700,
            minWidth: 18,
            textAlign: 'center',
            borderRadius: 999,
            padding: '1px 6px',
            background: 'var(--color-bg)',
            color: 'var(--color-on-surface)',
          }}
        >
          {count}
        </span>
      </div>
      {children}
    </div>
  );
}

function TicketCard({
  plate,
  svc,
  ready = false,
  children,
}: {
  readonly plate: string;
  readonly svc: string;
  readonly ready?: boolean;
  readonly children: React.ReactNode;
}) {
  return (
    <div
      style={{
        borderRadius: 10,
        padding: '8px 9px',
        background: 'var(--color-surface)',
        border: ready ? '1px solid var(--kilat-lume)' : '1px solid var(--hairline)',
        boxShadow: 'var(--shadow-1)',
        display: 'grid',
        gap: 5,
      }}
    >
      <span
        style={{
          fontSize: 12.5,
          fontWeight: 800,
          letterSpacing: 0.4,
          fontVariantNumeric: 'tabular-nums',
          color: 'var(--color-on-surface)',
        }}
      >
        {plate}
      </span>
      <span style={{ fontSize: 10.5, color: 'var(--color-on-surface-muted)' }}>{svc}</span>
      <span style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        {children}
      </span>
    </div>
  );
}

function ProgressBar({ value }: { readonly value: number }) {
  return (
    <span
      aria-hidden
      style={{
        flex: 1,
        minWidth: 40,
        height: 5,
        borderRadius: 999,
        background: 'var(--hairline-strong)',
        overflow: 'hidden',
      }}
    >
      <span
        style={{
          display: 'block',
          height: '100%',
          width: `${Math.round(value * 100)}%`,
          borderRadius: 999,
          background: 'var(--grad-lume)',
        }}
      />
    </span>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Customer PWA (phone) — white-label, demo operator's own accent
// ────────────────────────────────────────────────────────────────────────────

export function CustomerPhoneMock({ className }: { readonly className?: string }) {
  const { t } = useI18n();
  return (
    <PhoneFrame className={className} label={CUSTOMER_BRAND}>
      <div
        style={{
          height: '100%',
          background: `linear-gradient(180deg, ${CUSTOMER_ACCENT} 0%, #4b3fd4 100%)`,
          color: '#ffffff',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* app top (extra top padding clears the notch) */}
        <div style={{ padding: '36px 16px 8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span
              aria-hidden
              style={{
                width: 26,
                height: 26,
                borderRadius: 8,
                background: 'rgba(255,255,255,0.22)',
                display: 'grid',
                placeItems: 'center',
                fontWeight: 800,
                fontSize: 13,
              }}
            >
              {CUSTOMER_BRAND.charAt(0)}
            </span>
            <span style={{ fontWeight: 800, fontSize: 14 }}>{CUSTOMER_BRAND}</span>
            <span
              style={{
                marginLeft: 'auto',
                fontSize: 9.5,
                fontWeight: 700,
                padding: '3px 7px',
                borderRadius: 999,
                background: 'rgba(255,255,255,0.18)',
              }}
            >
              {t('marketing.home.mock.loyaltyTier')}
            </span>
          </div>
        </div>

        {/* loyalty card */}
        <div style={{ padding: '4px 14px' }}>
          <div
            style={{
              borderRadius: 16,
              padding: '14px 16px',
              background: 'rgba(255,255,255,0.14)',
              border: '1px solid rgba(255,255,255,0.22)',
              backdropFilter: 'blur(6px)',
              WebkitBackdropFilter: 'blur(6px)',
            }}
          >
            <span style={{ fontSize: 11, opacity: 0.85 }}>{t('marketing.home.mock.balance')}</span>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginTop: 2 }}>
              <span style={{ fontSize: 30, fontWeight: 800, letterSpacing: -0.5 }}>1,240</span>
              <span style={{ fontSize: 12, opacity: 0.9 }}>{t('marketing.home.mock.points')}</span>
            </div>
            <div
              aria-hidden
              style={{
                marginTop: 10,
                height: 6,
                borderRadius: 999,
                background: 'rgba(255,255,255,0.25)',
                overflow: 'hidden',
              }}
            >
              <span
                style={{
                  display: 'block',
                  height: '100%',
                  width: '78%',
                  background: '#ffffff',
                  borderRadius: 999,
                }}
              />
            </div>
          </div>
        </div>

        {/* reward + CTA on a light sheet */}
        <div
          style={{
            marginTop: 12,
            flex: 1,
            background: '#ffffff',
            color: '#1e2330',
            borderTopLeftRadius: 20,
            borderTopRightRadius: 20,
            padding: '16px 14px',
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
          }}
        >
          <div
            style={{
              display: 'flex',
              gap: 10,
              alignItems: 'center',
              padding: '11px 12px',
              borderRadius: 14,
              background: '#f4f2ff',
              border: `1px solid ${CUSTOMER_ACCENT}22`,
            }}
          >
            <span
              aria-hidden
              style={{
                width: 34,
                height: 34,
                borderRadius: 10,
                background: CUSTOMER_ACCENT,
                display: 'grid',
                placeItems: 'center',
                flexShrink: 0,
              }}
            >
              <GiftGlyph />
            </span>
            <span style={{ display: 'grid', gap: 1 }}>
              <span style={{ fontSize: 12.5, fontWeight: 800 }}>
                {t('marketing.home.mock.rewardTitle')}
              </span>
              <span style={{ fontSize: 10.5, color: '#6b7280' }}>
                {t('marketing.home.mock.rewardSub')}
              </span>
            </span>
          </div>

          <button
            type="button"
            tabIndex={-1}
            aria-hidden
            style={{
              border: 'none',
              borderRadius: 12,
              padding: '12px',
              background: CUSTOMER_ACCENT,
              color: '#ffffff',
              fontWeight: 800,
              fontSize: 13,
              cursor: 'default',
            }}
          >
            {t('marketing.home.mock.bookWash')}
          </button>

          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
              fontSize: 10,
              color: '#9096a3',
            }}
          >
            <DownloadGlyph />
            {t('marketing.home.mock.installed')}
          </span>
        </div>
      </div>
    </PhoneFrame>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Agent PWA (phone) — Kilat-branded
// ────────────────────────────────────────────────────────────────────────────

export function AgentPhoneMock({ className }: { readonly className?: string }) {
  const { t } = useI18n();
  return (
    <PhoneFrame className={className} label={t('marketing.home.mock.agentApp')}>
      <div
        style={{
          height: '100%',
          background: 'var(--hero-ground)',
          color: '#e8f2ec',
          display: 'flex',
          flexDirection: 'column',
          padding: '36px 14px 16px',
          gap: 12,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <BoltGlyph />
          <span style={{ fontWeight: 800, fontSize: 14 }}>{t('marketing.home.mock.agentApp')}</span>
          <span
            style={{
              marginLeft: 'auto',
              fontSize: 9.5,
              fontWeight: 800,
              padding: '3px 8px',
              borderRadius: 999,
              color: '#04140d',
              background: 'var(--kilat-lume)',
            }}
          >
            {t('marketing.home.mock.tierGold')}
          </span>
        </div>

        <div
          style={{
            borderRadius: 16,
            padding: '14px 16px',
            background: 'rgba(255,255,255,0.06)',
            border: '1px solid rgba(255,255,255,0.12)',
          }}
        >
          <span style={{ fontSize: 11, color: 'rgba(232,242,236,0.7)' }}>
            {t('marketing.home.mock.commissionEarned')}
          </span>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginTop: 2 }}>
            <span style={{ fontSize: 15, fontWeight: 700, opacity: 0.8 }}>RM</span>
            <span style={{ fontSize: 30, fontWeight: 800, letterSpacing: -0.5 }}>640</span>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <MiniStat value="8" label={t('marketing.home.mock.signupsThisMonth')} />
          <MiniStat value="RM 100" label={t('marketing.home.mock.pending')} />
        </div>

        <button
          type="button"
          tabIndex={-1}
          aria-hidden
          style={{
            marginTop: 'auto',
            border: 'none',
            borderRadius: 12,
            padding: '12px',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            background: 'var(--kilat-lume)',
            color: '#04140d',
            fontWeight: 800,
            fontSize: 13,
            cursor: 'default',
          }}
        >
          <QrGlyph />
          {t('marketing.home.mock.shareQr')}
        </button>
      </div>
    </PhoneFrame>
  );
}

function MiniStat({ value, label }: { readonly value: string; readonly label: string }) {
  return (
    <div
      style={{
        borderRadius: 12,
        padding: '10px 12px',
        background: 'rgba(255,255,255,0.05)',
        border: '1px solid rgba(255,255,255,0.1)',
      }}
    >
      <span style={{ fontSize: 18, fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </span>
      <span
        style={{
          display: 'block',
          fontSize: 9.5,
          color: 'rgba(232,242,236,0.66)',
          marginTop: 1,
          lineHeight: 1.3,
        }}
      >
        {label}
      </span>
    </div>
  );
}

// ── shared phone frame ──────────────────────────────────────────────────────

function PhoneFrame({
  label,
  className,
  children,
}: {
  readonly label: string;
  readonly className?: string;
  readonly children: React.ReactNode;
}) {
  return (
    <div
      className={className}
      role="img"
      aria-label={label}
      style={{
        width: 250,
        maxWidth: '100%',
        aspectRatio: '250 / 512',
        padding: 9,
        borderRadius: 40,
        background: 'linear-gradient(160deg, #23262e, #0a0b0e)',
        boxShadow: '0 44px 90px -36px rgba(0,0,0,0.72), inset 0 0 0 1px rgba(255,255,255,0.08)',
        position: 'relative',
      }}
    >
      {/* notch */}
      <span
        aria-hidden
        style={{
          position: 'absolute',
          top: 16,
          left: '50%',
          transform: 'translateX(-50%)',
          width: 74,
          height: 18,
          borderRadius: 999,
          background: '#000',
          zIndex: 2,
        }}
      />
      <div
        style={{
          height: '100%',
          borderRadius: 32,
          overflow: 'hidden',
          background: '#000',
        }}
      >
        {children}
      </div>
    </div>
  );
}

// ── glyphs (decorative, aria-hidden) ────────────────────────────────────────

function BoltGlyph() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden focusable="false">
      <path d="M13 2 4 14h6l-1 8 9-12h-6l1-8Z" fill="var(--kilat-lume)" />
    </svg>
  );
}
function GiftGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden focusable="false" fill="none">
      <path
        d="M4 11h16v9H4v-9ZM3 7h18v4H3V7Zm9 0v13M12 7S10.5 3 8.5 3 6 6 12 7Zm0 0s1.5-4 3.5-4S18 6 12 7Z"
        stroke="#ffffff"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
    </svg>
  );
}
function DownloadGlyph() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" aria-hidden focusable="false" fill="none">
      <path
        d="M12 3v12m0 0 4-4m-4 4-4-4M4 19h16"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
function QrGlyph() {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      aria-hidden
      focusable="false"
      fill="currentColor"
    >
      <path d="M3 3h7v7H3V3Zm2 2v3h3V5H5Zm9-2h7v7h-7V3Zm2 2v3h3V5h-3ZM3 14h7v7H3v-7Zm2 2v3h3v-3H5Zm9 0h3v3h-3v-3Zm4 4h3v1h-3v-1Zm0-6h1v3h-1v-3Z" />
    </svg>
  );
}
